import React, { useState } from 'react';
import { DollarSign, TrendingUp, MapPin, Calculator, BarChart3, Users, Target, Info, ArrowRight } from 'lucide-react';

const WealthComparisonApp = () => {
  const [wealth, setWealth] = useState('');
  const [income, setIncome] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [userCountry, setUserCountry] = useState('');
  const [results, setResults] = useState(null);

  // Exchange rates
  const exchangeRates = {
    USD: 1,
    AUD: 0.65,
    EUR: 1.08,
    GBP: 1.26,
    CAD: 0.74
  };

  // Global percentile data
  const globalWealthPercentiles = [
    { percentile: 10, value: 255 },
    { percentile: 25, value: 1540 },
    { percentile: 50, value: 8360 },
    { percentile: 75, value: 39080 },
    { percentile: 90, value: 149010 },
    { percentile: 95, value: 344692 },
    { percentile: 99, value: 1483170 },
    { percentile: 99.9, value: 13666560 }
  ];

  const globalIncomePercentiles = [
    { percentile: 10, value: 1100 },
    { percentile: 25, value: 3800 },
    { percentile: 50, value: 9733 },
    { percentile: 75, value: 24500 },
    { percentile: 90, value: 56000 },
    { percentile: 95, value: 89000 },
    { percentile: 99, value: 180000 },
    { percentile: 99.9, value: 500000 }
  ];

  // Comprehensive Country Database
  const countryDatabase = {
    // Rich Countries
    'Monaco': { medianWealth: 450000, medianIncome: 95000, category: 'rich' },
    'Luxembourg': { medianWealth: 395000, medianIncome: 78000, category: 'rich' },
    'Liechtenstein': { medianWealth: 320000, medianIncome: 80000, category: 'rich' },
    'Australia': { medianWealth: 268000, medianIncome: 54000, category: 'rich' },
    'Belgium': { medianWealth: 230548, medianIncome: 52000, category: 'rich' },
    'Hong Kong': { medianWealth: 173768, medianIncome: 48000, category: 'rich' },
    'New Zealand': { medianWealth: 171624, medianIncome: 42000, category: 'rich' },
    'Switzerland': { medianWealth: 167310, medianIncome: 85000, category: 'rich' },
    'Denmark': { medianWealth: 165622, medianIncome: 61000, category: 'rich' },
    'Netherlands': { medianWealth: 136105, medianIncome: 58000, category: 'rich' },
    'Canada': { medianWealth: 125688, medianIncome: 52000, category: 'rich' },
    'Japan': { medianWealth: 120493, medianIncome: 38000, category: 'rich' },
    'France': { medianWealth: 115046, medianIncome: 47000, category: 'rich' },
    'Norway': { medianWealth: 114234, medianIncome: 75000, category: 'rich' },
    'United Kingdom': { medianWealth: 108754, medianIncome: 46000, category: 'rich' },
    'United States': { medianWealth: 107740, medianIncome: 70000, category: 'rich' },
    'Ireland': { medianWealth: 104842, medianIncome: 50000, category: 'rich' },
    'Austria': { medianWealth: 91833, medianIncome: 51000, category: 'rich' },
    'Italy': { medianWealth: 91889, medianIncome: 35000, category: 'rich' },
    'Sweden': { medianWealth: 89846, medianIncome: 54000, category: 'rich' },
    'Singapore': { medianWealth: 86717, medianIncome: 68000, category: 'rich' },
    'Andorra': { medianWealth: 85000, medianIncome: 45000, category: 'rich' },
    'Qatar': { medianWealth: 85000, medianIncome: 75000, category: 'rich' },
    'Taiwan': { medianWealth: 78000, medianIncome: 46000, category: 'rich' },
    'United Arab Emirates': { medianWealth: 75000, medianIncome: 65000, category: 'rich' },
    'San Marino': { medianWealth: 75000, medianIncome: 42000, category: 'rich' },
    'Finland': { medianWealth: 73775, medianIncome: 48000, category: 'rich' },
    'South Korea': { medianWealth: 72198, medianIncome: 42000, category: 'rich' },
    'Spain': { medianWealth: 69143, medianIncome: 38000, category: 'rich' },
    'Kuwait': { medianWealth: 65000, medianIncome: 55000, category: 'rich' },
    'Germany': { medianWealth: 65374, medianIncome: 54000, category: 'rich' },
    'Iceland': { medianWealth: 65000, medianIncome: 68000, category: 'rich' },
    'Israel': { medianWealth: 62206, medianIncome: 44000, category: 'rich' },
    'Faroe Islands': { medianWealth: 45000, medianIncome: 55000, category: 'rich' },
    'Bahrain': { medianWealth: 45000, medianIncome: 35000, category: 'rich' },
    'Brunei': { medianWealth: 45000, medianIncome: 40000, category: 'rich' },

    // Middle Income Countries
    'Greece': { medianWealth: 54261, medianIncome: 20000, category: 'middle' },
    'Slovenia': { medianWealth: 44932, medianIncome: 32000, category: 'middle' },
    'Portugal': { medianWealth: 44025, medianIncome: 24000, category: 'middle' },
    'Saudi Arabia': { medianWealth: 35000, medianIncome: 28000, category: 'middle' },
    'Estonia': { medianWealth: 28112, medianIncome: 29000, category: 'middle' },
    'Chile': { medianWealth: 27606, medianIncome: 24000, category: 'middle' },
    'Czech Republic': { medianWealth: 26789, medianIncome: 28000, category: 'middle' },
    'China': { medianWealth: 26752, medianIncome: 18000, category: 'middle' },
    'Croatia': { medianWealth: 26277, medianIncome: 18000, category: 'middle' },
    'Poland': { medianWealth: 26355, medianIncome: 31000, category: 'middle' },
    'Oman': { medianWealth: 25000, medianIncome: 22000, category: 'middle' },
    'Slovakia': { medianWealth: 19783, medianIncome: 26000, category: 'middle' },
    'Bahamas': { medianWealth: 18000, medianIncome: 22000, category: 'middle' },
    'Lithuania': { medianWealth: 16096, medianIncome: 24000, category: 'middle' },
    'French Guiana': { medianWealth: 15000, medianIncome: 18000, category: 'middle' },
    'Hungary': { medianWealth: 15112, medianIncome: 23000, category: 'middle' },
    'Turkey': { medianWealth: 15112, medianIncome: 17000, category: 'middle' },
    'Latvia': { medianWealth: 14260, medianIncome: 21000, category: 'middle' },
    'Mexico': { medianWealth: 12850, medianIncome: 16000, category: 'middle' },
    'Romania': { medianWealth: 12464, medianIncome: 16000, category: 'middle' },
    'Barbados': { medianWealth: 12000, medianIncome: 15000, category: 'middle' },
    'Russia': { medianWealth: 12198, medianIncome: 15000, category: 'middle' },
    'Thailand': { medianWealth: 9571, medianIncome: 13000, category: 'middle' },
    'Brazil': { medianWealth: 9140, medianIncome: 14000, category: 'middle' },
    'Malaysia': { medianWealth: 8908, medianIncome: 19000, category: 'middle' },
    'Bulgaria': { medianWealth: 8842, medianIncome: 12000, category: 'middle' },
    'Uruguay': { medianWealth: 8500, medianIncome: 15000, category: 'middle' },
    'Trinidad and Tobago': { medianWealth: 8500, medianIncome: 12000, category: 'middle' },
    'Lebanon': { medianWealth: 8500, medianIncome: 8000, category: 'middle' },
    'Mauritius': { medianWealth: 7500, medianIncome: 11000, category: 'middle' },
    'Argentina': { medianWealth: 7345, medianIncome: 19000, category: 'middle' },
    'Montenegro': { medianWealth: 7200, medianIncome: 10000, category: 'middle' },
    'Colombia': { medianWealth: 6837, medianIncome: 12000, category: 'middle' },
    'Kazakhstan': { medianWealth: 6800, medianIncome: 12000, category: 'middle' },
    'Serbia': { medianWealth: 6500, medianIncome: 9000, category: 'middle' },
    'Panama': { medianWealth: 6200, medianIncome: 14000, category: 'middle' },
    'Bosnia and Herzegovina': { medianWealth: 5800, medianIncome: 8000, category: 'middle' },
    'Belarus': { medianWealth: 5400, medianIncome: 8000, category: 'middle' },
    'North Macedonia': { medianWealth: 4900, medianIncome: 7500, category: 'middle' },
    'Costa Rica': { medianWealth: 4800, medianIncome: 12000, category: 'middle' },
    'South Africa': { medianWealth: 4833, medianIncome: 11000, category: 'middle' },
    'Peru': { medianWealth: 4794, medianIncome: 9000, category: 'middle' },
    'Jordan': { medianWealth: 4500, medianIncome: 6500, category: 'middle' },
    'Dominican Republic': { medianWealth: 4500, medianIncome: 8000, category: 'middle' },
    'Suriname': { medianWealth: 4500, medianIncome: 8000, category: 'middle' },
    'Albania': { medianWealth: 4200, medianIncome: 7000, category: 'middle' },
    'Azerbaijan': { medianWealth: 3800, medianIncome: 8500, category: 'middle' },
    'Ecuador': { medianWealth: 3800, medianIncome: 7500, category: 'middle' },
    'Jamaica': { medianWealth: 3500, medianIncome: 6000, category: 'middle' },
    'Iran': { medianWealth: 3500, medianIncome: 8000, category: 'middle' },
    'El Salvador': { medianWealth: 3500, medianIncome: 6500, category: 'middle' },
    'Tunisia': { medianWealth: 3400, medianIncome: 5000, category: 'middle' },
    'Tonga': { medianWealth: 3200, medianIncome: 5500, category: 'middle' },
    'Georgia': { medianWealth: 3200, medianIncome: 6000, category: 'middle' },
    'Guyana': { medianWealth: 3200, medianIncome: 6500, category: 'middle' },
    'Morocco': { medianWealth: 2900, medianIncome: 4500, category: 'middle' },
    'Armenia': { medianWealth: 2800, medianIncome: 5500, category: 'middle' },
    'Guatemala': { medianWealth: 2800, medianIncome: 5500, category: 'middle' },
    'Paraguay': { medianWealth: 2800, medianIncome: 6000, category: 'middle' },
    'Fiji': { medianWealth: 2800, medianIncome: 6000, category: 'middle' },

    // Poor Countries
    'Vietnam': { medianWealth: 3026, medianIncome: 6500, category: 'poor' },
    'India': { medianWealth: 3042, medianIncome: 6700, category: 'poor' },
    'Cuba': { medianWealth: 2500, medianIncome: 4000, category: 'poor' },
    'Samoa': { medianWealth: 2500, medianIncome: 4500, category: 'poor' },
    'Honduras': { medianWealth: 2200, medianIncome: 4000, category: 'poor' },
    'Bolivia': { medianWealth: 2200, medianIncome: 4500, category: 'poor' },
    'Marshall Islands': { medianWealth: 2200, medianIncome: 4000, category: 'poor' },
    'Philippines': { medianWealth: 2174, medianIncome: 7000, category: 'poor' },
    'Iraq': { medianWealth: 2100, medianIncome: 6000, category: 'poor' },
    'Egypt': { medianWealth: 2100, medianIncome: 4000, category: 'poor' },
    'Tuvalu': { medianWealth: 2000, medianIncome: 3800, category: 'poor' },
    'Micronesia': { medianWealth: 1900, medianIncome: 3500, category: 'poor' },
    'Sri Lanka': { medianWealth: 1800, medianIncome: 4500, category: 'poor' },
    'Nicaragua': { medianWealth: 1800, medianIncome: 3500, category: 'poor' },
    'Kiribati': { medianWealth: 1800, medianIncome: 3200, category: 'poor' },
    'Indonesia': { medianWealth: 1649, medianIncome: 8000, category: 'poor' },
    'Bhutan': { medianWealth: 1500, medianIncome: 3500, category: 'poor' },
    'Venezuela': { medianWealth: 1500, medianIncome: 3000, category: 'poor' },
    'Bangladesh': { medianWealth: 1385, medianIncome: 3200, category: 'poor' },
    'Ghana': { medianWealth: 1260, medianIncome: 4200, category: 'poor' },
    'Nigeria': { medianWealth: 1235, medianIncome: 4500, category: 'poor' },
    'Pakistan': { medianWealth: 1230, medianIncome: 3500, category: 'poor' },
    'Mongolia': { medianWealth: 1200, medianIncome: 4000, category: 'poor' },
    'Vanuatu': { medianWealth: 1200, medianIncome: 3000, category: 'poor' },
    'Cambodia': { medianWealth: 1167, medianIncome: 3200, category: 'poor' },
    'Laos': { medianWealth: 978, medianIncome: 2600, category: 'poor' },
    'Papua New Guinea': { medianWealth: 950, medianIncome: 2800, category: 'poor' },
    'Kenya': { medianWealth: 950, medianIncome: 3500, category: 'poor' },
    'Nepal': { medianWealth: 905, medianIncome: 2800, category: 'poor' },
    'Myanmar': { medianWealth: 825, medianIncome: 2100, category: 'poor' },
    'Timor-Leste': { medianWealth: 800, medianIncome: 2000, category: 'poor' },
    'Senegal': { medianWealth: 743, medianIncome: 2800, category: 'poor' },
    'Solomon Islands': { medianWealth: 650, medianIncome: 1800, category: 'poor' },
    'Afghanistan': { medianWealth: 645, medianIncome: 1500, category: 'poor' },
    'Tanzania': { medianWealth: 635, medianIncome: 2400, category: 'poor' },
    'Rwanda': { medianWealth: 567, medianIncome: 2200, category: 'poor' },
    'Zambia': { medianWealth: 520, medianIncome: 2000, category: 'poor' },
    'Ethiopia': { medianWealth: 481, medianIncome: 1800, category: 'poor' },
    'Uganda': { medianWealth: 456, medianIncome: 1900, category: 'poor' },
    'Haiti': { medianWealth: 456, medianIncome: 1800, category: 'poor' },
    'Syria': { medianWealth: 450, medianIncome: 1500, category: 'poor' },
    'Zimbabwe': { medianWealth: 445, medianIncome: 1800, category: 'poor' },
    'Benin': { medianWealth: 420, medianIncome: 1600, category: 'poor' },
    'Mali': { medianWealth: 412, medianIncome: 1600, category: 'poor' },
    'North Korea': { medianWealth: 400, medianIncome: 1000, category: 'poor' },
    'Madagascar': { medianWealth: 394, medianIncome: 1100, category: 'poor' },
    'Burkina Faso': { medianWealth: 385, medianIncome: 1700, category: 'poor' },
    'Yemen': { medianWealth: 380, medianIncome: 1200, category: 'poor' },
    'Togo': { medianWealth: 380, medianIncome: 1500, category: 'poor' },
    'Guinea': { medianWealth: 350, medianIncome: 1400, category: 'poor' },
    'Liberia': { medianWealth: 320, medianIncome: 1200, category: 'poor' },
    'Chad': { medianWealth: 318, medianIncome: 1300, category: 'poor' },
    'Malawi': { medianWealth: 315, medianIncome: 900, category: 'poor' },
    'Mozambique': { medianWealth: 298, medianIncome: 1200, category: 'poor' },
    'Niger': { medianWealth: 290, medianIncome: 1000, category: 'poor' },
    'Sierra Leone': { medianWealth: 280, medianIncome: 1100, category: 'poor' },
    'Central African Republic': { medianWealth: 250, medianIncome: 800, category: 'poor' },
    'Democratic Republic of Congo': { medianWealth: 210, medianIncome: 700, category: 'poor' },
    'Somalia': { medianWealth: 200, medianIncome: 650, category: 'poor' },
    'Burundi': { medianWealth: 185, medianIncome: 600, category: 'poor' }
  };

  const convertToUSD = (amount, fromCurrency) => {
    return amount * exchangeRates[fromCurrency];
  };

  const calculatePercentile = (value, percentileArray) => {
    if (value <= 0) return 0;
    
    for (let i = 0; i < percentileArray.length; i++) {
      if (value <= percentileArray[i].value) {
        if (i === 0) {
          return (value / percentileArray[i].value) * percentileArray[i].percentile;
        }
        const prev = percentileArray[i-1];
        const curr = percentileArray[i];
        
        return prev.percentile + 
               ((value - prev.value) / (curr.value - prev.value)) * 
               (curr.percentile - prev.percentile);
      }
    }
    
    const lastItem = percentileArray[percentileArray.length - 1];
    return Math.min(99.9, lastItem.percentile + Math.log(value / lastItem.value) * 0.5);
  };

  const calculateCountryPercentile = (value, countryMedian) => {
    const ratio = value / countryMedian;
    
    if (ratio < 0.1) return 5;
    if (ratio < 0.3) return 15;
    if (ratio < 0.5) return 25;
    if (ratio < 0.7) return 35;
    if (ratio < 1) return 45;
    if (ratio < 1.5) return 60;
    if (ratio < 2) return 70;
    if (ratio < 3) return 80;
    if (ratio < 5) return 90;
    if (ratio < 10) return 95;
    return 99;
  };

  const getRandomCountriesByCategory = (category, exclude = null) => {
    const countries = Object.entries(countryDatabase)
      .filter(([name, data]) => data.category === category && name !== exclude)
      .map(([name, data]) => ({ name, ...data }));
    
    const shuffled = countries.sort(() => 0.5 - Math.random());
    return shuffled.slice(0, 2);
  };

  const handleCalculate = () => {
    const wealthValue = parseFloat(wealth) || 0;
    const incomeValue = parseFloat(income) || 0;
    
    const wealthUSD = convertToUSD(wealthValue, currency);
    const incomeUSD = convertToUSD(incomeValue, currency);
    
    const globalWealthPercentile = calculatePercentile(wealthUSD, globalWealthPercentiles);
    const globalIncomePercentile = calculatePercentile(incomeUSD, globalIncomePercentiles);
    
    const userCountryData = userCountry ? countryDatabase[userCountry] : null;
    
    const poorCountries = getRandomCountriesByCategory('poor', userCountry);
    const middleCountries = getRandomCountriesByCategory('middle', userCountry);
    const richCountries = getRandomCountriesByCategory('rich', userCountry);
    
    const countryResults = {
      user: userCountryData ? {
        name: userCountry,
        ...userCountryData,
        wealthPercentile: calculateCountryPercentile(wealthUSD, userCountryData.medianWealth),
        incomePercentile: calculateCountryPercentile(incomeUSD, userCountryData.medianIncome)
      } : null,
      poor: poorCountries.map(country => ({
        ...country,
        wealthPercentile: calculateCountryPercentile(wealthUSD, country.medianWealth),
        incomePercentile: calculateCountryPercentile(incomeUSD, country.medianIncome)
      })),
      middle: middleCountries.map(country => ({
        ...country,
        wealthPercentile: calculateCountryPercentile(wealthUSD, country.medianWealth),
        incomePercentile: calculateCountryPercentile(incomeUSD, country.medianIncome)
      })),
      rich: richCountries.map(country => ({
        ...country,
        wealthPercentile: calculateCountryPercentile(wealthUSD, country.medianWealth),
        incomePercentile: calculateCountryPercentile(incomeUSD, country.medianIncome)
      }))
    };

    setResults({
      wealthUSD,
      incomeUSD,
      globalWealthPercentile,
      globalIncomePercentile,
      countryResults
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getPercentileColor = (percentile) => {
    if (percentile >= 90) return '#059669';
    if (percentile >= 75) return '#16a34a';
    if (percentile >= 50) return '#2563eb';
    if (percentile >= 25) return '#ea580c';
    return '#dc2626';
  };

  const getPercentileBackground = (percentile) => {
    if (percentile >= 90) return { backgroundColor: '#ecfdf5', borderColor: '#a7f3d0' };
    if (percentile >= 75) return { backgroundColor: '#f0fdf4', borderColor: '#bbf7d0' };
    if (percentile >= 50) return { backgroundColor: '#eff6ff', borderColor: '#bfdbfe' };
    if (percentile >= 25) return { backgroundColor: '#fff7ed', borderColor: '#fed7aa' };
    return { backgroundColor: '#fef2f2', borderColor: '#fecaca' };
  };

  const getPercentileCategory = (percentile) => {
    if (percentile >= 90) return 'Top 10%';
    if (percentile >= 75) return 'Upper Quarter';
    if (percentile >= 50) return 'Above Median';
    if (percentile >= 25) return 'Lower Half';
    return 'Bottom Quarter';
  };

  const countryOptions = Object.keys(countryDatabase).sort();

  return (
    <div style={{
      minHeight: '100vh',
      background: 'white'
    }}>
      {/* Header */}
      <div style={{
        backgroundColor: 'white',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        borderBottom: '1px solid #e5e7eb'
      }}>
        <div style={{
          maxWidth: '1280px',
          margin: '0 auto',
          padding: '2rem 1rem'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              marginBottom: '1.5rem'
            }}>
              <div>
                <img 
                  src="/logo.png" 
                  alt="logo" 
                  style={{ width: "180px", height: "auto", marginBottom: "-50px" }} 
                />
              </div>
            </div>
            <h1 style={{
              fontSize: '2.25rem',
              fontWeight: 'bold',
              color: '#111827',
              marginBottom: '1rem'
            }}>
              Global Wealth Comparison Platform
            </h1>
            <p style={{
              fontSize: '1.25rem',
              color: '#6b7280',
              maxWidth: '48rem',
              margin: '0 auto',
              lineHeight: '1.6'
            }}>
              Compare your wealth with the rest of the world!
            </p>
          </div>
        </div>
      </div>

      <div style={{
        maxWidth: '1280px',
        margin: '0 auto',
        padding: '3rem 1rem'
      }}>
        {/* Input Form */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '1rem',
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
          border: '1px solid #e5e7eb',
          overflow: 'hidden',
          marginBottom: '3rem'
        }}>
          <div style={{
            background: 'linear-gradient(to right, #006838, #006130)',
            padding: '1.5rem 2rem',
            color: 'white'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              <Calculator size={24} />
              <h2 style={{
                fontSize: '1.5rem',
                fontWeight: '600',
                margin: 0
              }}>Your Finances</h2>
            </div>
            <p style={{
              color: 'white',
              marginTop: '0.5rem',
              margin: '0.5rem 0 0 0'
            }}>Enter your financial information for comparison against the rest of the world</p>
          </div>
          
          <div style={{ padding: '2rem' }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '1.5rem'
            }}>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem'
              }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '0.75rem'
                }}>
                  <DollarSign size={16} style={{ marginRight: '0.5rem' }} />
                  Net Worth / Total Wealth
                </label>
                <input
                  type="number"
                  value={wealth}
                  onChange={(e) => setWealth(e.target.value)}
                  placeholder="Enter total wealth"
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.5rem',
                    fontSize: '1.125rem',
                    transition: 'all 0.2s',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem'
              }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '0.75rem'
                }}>
                  <TrendingUp size={16} style={{ marginRight: '0.5rem' }} />
                  Annual Income
                </label>
                <input
                  type="number"
                  value={income}
                  onChange={(e) => setIncome(e.target.value)}
                  placeholder="Enter annual income"
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.5rem',
                    fontSize: '1.125rem',
                    transition: 'all 0.2s',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem'
              }}>
                <label style={{
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '0.75rem',
                  display: 'block'
                }}>Currency</label>
                <select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.5rem',
                    fontSize: '1.125rem',
                    backgroundColor: 'white',
                    transition: 'all 0.2s',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                >
                  <option value="USD">USD ($)</option>
                  <option value="AUD">AUD (A$)</option>
                  <option value="EUR">EUR (€)</option>
                  <option value="GBP">GBP (£)</option>
                  <option value="CAD">CAD (C$)</option>
                </select>
                
              </div>

              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem'
              }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '0.75rem'
                }}>
                  <MapPin size={16} style={{ marginRight: '0.5rem' }} />
                  Country of Residence
                </label>
                <select
                  value={userCountry}
                  onChange={(e) => setUserCountry(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.5rem',
                    fontSize: '1.125rem',
                    backgroundColor: 'white',
                    transition: 'all 0.2s',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                >
                  <option value="">Select your country</option>
                  {countryOptions.map(country => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </select>
              
              </div>
            </div>
            
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: '2rem'
            }}>
              <button
                onClick={handleCalculate}
                disabled={!wealth && !income}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '1rem 2rem',
                  backgroundColor: (!wealth && !income) ? 'black' : '#006130',
                  color: 'white',
                  fontWeight: '600',
                  borderRadius: '0.75rem',
                  border: 'none',
                  cursor: (!wealth && !income) ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)',
                  transform: 'translateY(0)'
                }}
              >
                <BarChart3 size={20} style={{ marginRight: '0.5rem' }} />
                See how you compare 👀
                <ArrowRight size={20} style={{ marginLeft: '0.5rem' }} />
              </button>
            </div>
          </div>
        </div>

        {/* Results */}
        {results && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '2rem'
          }}>
            {/* Executive Summary */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '1rem',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
              border: '1px solid #e5e7eb',
              overflow: 'hidden'
            }}>
              <div style={{
                background: 'linear-gradient(to right, #006130, #006130)',
                padding: '1.5rem 2rem',
                color: 'white'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem'
                }}>
                  <Target size={24} />
                  <h2 style={{
                    fontSize: '1.5rem',
                    fontWeight: '600',
                    margin: 0
                  }}>Overall Summary</h2>
                </div>
                <p style={{
                  color: 'white',
                  marginTop: '0.5rem',
                  margin: '0.5rem 0 0 0'
                }}>Your global financial position overview</p>
              </div>
              
              <div style={{ padding: '2rem' }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '1.5rem'
                }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{
                      backgroundColor: '#f9fafb',
                      borderRadius: '0.75rem',
                      padding: '1.5rem',
                      border: '1px solid #e5e7eb'
                    }}>
                      <p style={{
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        color: '#6b7280',
                        marginBottom: '0.5rem'
                      }}>Net Worth (USD)</p>
                      <p style={{
                        fontSize: '1.875rem',
                        fontWeight: 'bold',
                        color: '#111827'
                      }}>{formatCurrency(results.wealthUSD)}</p>
                    </div>
                  </div>
                  
                  <div style={{ textAlign: 'center' }}>
                    <div style={{
                      backgroundColor: '#f9fafb',
                      borderRadius: '0.75rem',
                      padding: '1.5rem',
                      border: '1px solid #e5e7eb'
                    }}>
                      <p style={{
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        color: '#6b7280',
                        marginBottom: '0.5rem'
                      }}>Annual Income (USD)</p>
                      <p style={{
                        fontSize: '1.875rem',
                        fontWeight: 'bold',
                        color: '#111827'
                      }}>{formatCurrency(results.incomeUSD)}</p>
                    </div>
                  </div>
                  
                  <div style={{ textAlign: 'center' }}>
                    <div style={{
                      borderRadius: '0.75rem',
                      padding: '1.5rem',
                      border: '2px solid',
                      ...getPercentileBackground(results.globalWealthPercentile)
                    }}>
                      <p style={{
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        color: '#6b7280',
                        marginBottom: '0.25rem'
                      }}>Global Wealth Ranking</p>
                      <p style={{
                        fontSize: '2.5rem',
                        fontWeight: 'bold',
                        color: getPercentileColor(results.globalWealthPercentile)
                      }}>
                        {results.globalWealthPercentile.toFixed(1)}%
                      </p>
                      <p style={{
                        fontSize: '0.75rem',
                        color: '#6b7280',
                        marginTop: '0.25rem'
                      }}>{getPercentileCategory(results.globalWealthPercentile)}</p>
                      <p style={{
                        fontSize: '0.75rem',
                        color: '#4b5563',
                        marginTop: '0.5rem'
                      }}>
                        Wealthier than {results.globalWealthPercentile.toFixed(1)}% of global population
                      </p>
                    </div>
                  </div>
                  
                  <div style={{ textAlign: 'center' }}>
                    <div style={{
                      borderRadius: '0.75rem',
                      padding: '1.5rem',
                      border: '2px solid',
                      ...getPercentileBackground(results.globalIncomePercentile)
                    }}>
                      <p style={{
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        color: '#6b7280',
                        marginBottom: '0.25rem'
                      }}>Global Income Ranking</p>
                      <p style={{
                        fontSize: '2.5rem',
                        fontWeight: 'bold',
                        color: getPercentileColor(results.globalIncomePercentile)
                      }}>
                        {results.globalIncomePercentile.toFixed(1)}%
                      </p>
                      <p style={{
                        fontSize: '0.75rem',
                        color: '#6b7280',
                        marginTop: '0.25rem'
                      }}>{getPercentileCategory(results.globalIncomePercentile)}</p>
                      <p style={{
                        fontSize: '0.75rem',
                        color: '#4b5563',
                        marginTop: '0.5rem'
                      }}>
                        Higher income than {results.globalIncomePercentile.toFixed(1)}% of global population
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* National Analysis */}
            {results.countryResults.user && (
              <div style={{
                backgroundColor: 'white',
                borderRadius: '1rem',
                boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
                border: '1px solid #e5e7eb',
                overflow: 'hidden'
              }}>
                <div style={{
                  background: 'linear-gradient(to right, #006130, #006130)',
                  padding: '1.5rem 2rem',
                  color: 'white'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem'
                  }}>
                    <MapPin size={24} />
                    <h2 style={{
                      fontSize: '1.5rem',
                      fontWeight: '600',
                      margin: 0
                    }}>National Analysis: {results.countryResults.user.name}</h2>
                  </div>
                  <p style={{
                    color: 'white',
                    marginTop: '0.5rem',
                    margin: '0.5rem 0 0 0'
                  }}>Your financial position within your country</p>
                </div>
                
                <div style={{ padding: '2rem' }}>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                    gap: '2rem'
                  }}>
                    <div style={{
                      borderRadius: '0.75rem',
                      padding: '1.5rem',
                      border: '2px solid',
                      ...getPercentileBackground(results.countryResults.user.wealthPercentile)
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '1rem'
                      }}>
                        <h3 style={{
                          fontSize: '1.125rem',
                          fontWeight: '600',
                          color: '#1f2937'
                        }}>Wealth Position</h3>
                        <DollarSign size={20} color="#6b7280" />
                      </div>
                      
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '1rem'
                      }}>
                        <div>
                          <p style={{
                            fontSize: '0.875rem',
                            color: '#6b7280'
                          }}>Your National Percentile</p>
                          <p style={{
                            fontSize: '2.5rem',
                            fontWeight: 'bold',
                            color: getPercentileColor(results.countryResults.user.wealthPercentile)
                          }}>
                            {results.countryResults.user.wealthPercentile}th
                          </p>
                          <p style={{
                            fontSize: '0.75rem',
                            color: '#6b7280',
                            marginTop: '0.25rem'
                          }}>{getPercentileCategory(results.countryResults.user.wealthPercentile)} nationally</p>
                        </div>
                        
                        <div style={{
                          paddingTop: '1rem',
                          borderTop: '1px solid #e5e7eb'
                        }}>
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}>
                            <span style={{
                              fontSize: '0.875rem',
                              color: '#6b7280'
                            }}>National Median:</span>
                            <span style={{
                              fontWeight: '600',
                              color: '#1f2937'
                            }}>{formatCurrency(results.countryResults.user.medianWealth)}</span>
                          </div>
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginTop: '0.25rem'
                          }}>
                            <span style={{
                              fontSize: '0.875rem',
                              color: '#6b7280'
                            }}>Your Wealth:</span>
                            <span style={{
                              fontWeight: '600',
                              color: '#1f2937'
                            }}>{formatCurrency(results.wealthUSD)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div style={{
                      borderRadius: '0.75rem',
                      padding: '1.5rem',
                      border: '2px solid',
                      ...getPercentileBackground(results.countryResults.user.incomePercentile)
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '1rem'
                      }}>
                        <h3 style={{
                          fontSize: '1.125rem',
                          fontWeight: '600',
                          color: '#1f2937'
                        }}>Income Position</h3>
                        <TrendingUp size={20} color="#6b7280" />
                      </div>
                      
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '1rem'
                      }}>
                        <div>
                          <p style={{
                            fontSize: '0.875rem',
                            color: '#6b7280'
                          }}>Your National Percentile</p>
                          <p style={{
                            fontSize: '2.5rem',
                            fontWeight: 'bold',
                            color: getPercentileColor(results.countryResults.user.incomePercentile)
                          }}>
                            {results.countryResults.user.incomePercentile}th
                          </p>
                          <p style={{
                            fontSize: '0.75rem',
                            color: '#6b7280',
                            marginTop: '0.25rem'
                          }}>{getPercentileCategory(results.countryResults.user.incomePercentile)} nationally</p>
                        </div>
                        
                        <div style={{
                          paddingTop: '1rem',
                          borderTop: '1px solid #e5e7eb'
                        }}>
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}>
                            <span style={{
                              fontSize: '0.875rem',
                              color: '#6b7280'
                            }}>National Median:</span>
                            <span style={{
                              fontWeight: '600',
                              color: '#1f2937'
                            }}>{formatCurrency(results.countryResults.user.medianIncome)}</span>
                          </div>
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginTop: '0.25rem'
                          }}>
                            <span style={{
                              fontSize: '0.875rem',
                              color: '#6b7280'
                            }}>Your Income:</span>
                            <span style={{
                              fontWeight: '600',
                              color: '#1f2937'
                            }}>{formatCurrency(results.incomeUSD)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* International Comparisons */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '1rem',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
              border: '1px solid #e5e7eb',
              overflow: 'hidden'
            }}>
              <div style={{
                background: '#006130',
                padding: '1.5rem 2rem',
                color: 'white'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem'
                }}>
                  <Users size={24} />
                  <h2 style={{
                    fontSize: '1.5rem',
                    fontWeight: '600',
                    margin: 0
                  }}>International Comparisons</h2>
                </div>
                <p style={{
                  color: 'white',
                  marginTop: '0.5rem',
                  margin: '0.5rem 0 0 0'
                }}>How you would rank in different countries</p>
              </div>
              
              <div style={{ padding: '2rem' }}>
                {['rich', 'middle', 'poor'].map((category) => (
                  <div key={category} style={{ marginBottom: '2rem' }}>
                    <h3 style={{
                      fontSize: '1.25rem',
                      fontWeight: '600',
                      color: '#1f2937',
                      marginBottom: '1rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      <div style={{
                        width: '1rem',
                        height: '1rem',
                        borderRadius: '50%',
                        backgroundColor: category === 'rich' ? 'black' : 
                                        category === 'middle' ? '#006130' : 'black'
                      }}></div>
                      {category === 'rich' ? 'High-Income Economies' : 
                      category === 'middle' ? 'Middle-Income Economies' : 'Developing Economies'}
                    </h3>
                    
                    {/* Mobile-friendly grid */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                      gap: '1.5rem'
                    }}>
                      {results.countryResults[category].map((country, index) => (
                        <div key={index} style={{
                          border: '1px solid #e5e7eb',
                          borderRadius: '0.75rem',
                          padding: '1rem',
                          transition: 'all 0.2s'
                        }}>
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            marginBottom: '1rem',
                            flexWrap: 'wrap'
                          }}>
                            <h4 style={{
                              fontSize: '1.125rem',
                              fontWeight: '600',
                              color: '#1f2937',
                              marginBottom: '0.5rem'
                            }}>{country.name}</h4>
                            <div style={{ textAlign: 'right' }}>
                              <p style={{
                                fontSize: '0.75rem',
                                color: '#6b7280',
                                marginBottom: '0.25rem'
                              }}>Economic Category</p>
                              <p style={{
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                color: '#374151',
                                textTransform: 'capitalize'
                              }}>{country.category} Income</p>
                            </div>
                          </div>
                          
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr',
                            gap: '1rem'
                          }}>
                            {/* Wealth Box */}
                            <div style={{
                              borderRadius: '0.5rem',
                              padding: '1rem',
                              border: '1px solid',
                              ...getPercentileBackground(country.wealthPercentile)
                            }}>
                              <p style={{
                                fontSize: '0.75rem',
                                fontWeight: '500',
                                color: '#6b7280',
                                marginBottom: '0.25rem'
                              }}>Wealth Ranking</p>
                              <p style={{
                                fontSize: '1.5rem',
                                fontWeight: 'bold',
                                color: getPercentileColor(country.wealthPercentile)
                              }}>
                                {country.wealthPercentile}th
                              </p>
                              <div style={{
                                marginTop: '0.5rem',
                                paddingTop: '0.5rem',
                                borderTop: '1px solid #e5e7eb'
                              }}>
                                <p style={{
                                  fontSize: '0.75rem',
                                  color: '#6b7280'
                                }}>National Median</p>
                                <p style={{
                                  fontSize: '0.875rem',
                                  fontWeight: '600',
                                  color: '#1f2937'
                                }}>{formatCurrency(country.medianWealth)}</p>
                              </div>
                            </div>

                            {/* Income Box */}
                            <div style={{
                              borderRadius: '0.5rem',
                              padding: '1rem',
                              border: '1px solid',
                              ...getPercentileBackground(country.incomePercentile)
                            }}>
                              <p style={{
                                fontSize: '0.75rem',
                                fontWeight: '500',
                                color: '#6b7280',
                                marginBottom: '0.25rem'
                              }}>Income Ranking</p>
                              <p style={{
                                fontSize: '1.5rem',
                                fontWeight: 'bold',
                                color: getPercentileColor(country.incomePercentile)
                              }}>
                                {country.incomePercentile}th
                              </p>
                              <div style={{
                                marginTop: '0.5rem',
                                paddingTop: '0.5rem',
                                borderTop: '1px solid #e5e7eb'
                              }}>
                                <p style={{
                                  fontSize: '0.75rem',
                                  color: '#6b7280'
                                }}>National Median</p>
                                <p style={{
                                  fontSize: '0.875rem',
                                  fontWeight: '600',
                                  color: '#1f2937'
                                }}>{formatCurrency(country.medianIncome)}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>


            {/* Methodology & Insights */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '1rem',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
              border: '1px solid #e5e7eb',
              overflow: 'hidden'
            }}>
              <div style={{
                background: '#006130',
                padding: '1.5rem 2rem',
                color: 'white'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem'
                }}>
                  <Info size={24} />
                  <h2 style={{
                    fontSize: '1.5rem',
                    fontWeight: '600',
                    margin: 0
                  }}>Methodology & Key Insights</h2>
                </div>
                <p style={{
                  color: 'white',
                  marginTop: '0.5rem',
                  margin: '0.5rem 0 0 0'
                }}>Understanding the analysis and global wealth patterns</p>
              </div>
              
              <div style={{ padding: '2rem' }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                  gap: '2rem'
                }}>
                  <div>
                    <h3 style={{
                      fontSize: '1.125rem',
                      fontWeight: '600',
                      color: '#1f2937',
                      marginBottom: '1rem'
                    }}>Data Sources & Methodology</h3>
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.75rem'
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '0.75rem'
                      }}>
                        <div style={{
                          width: '0.5rem',
                          height: '0.5rem',
                          borderRadius: '50%',
                          marginTop: '0.5rem',
                          flexShrink: 0,
                          backgroundColor: 'black'
                        }}></div>
                        <p style={{
                          fontSize: '0.875rem',
                          color: '#6b7280'
                        }}>Based on UBS Global Wealth Report 2024 - the most comprehensive global wealth study available</p>
                      </div>
                      <div style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '0.75rem'
                      }}>
                        <div style={{
                          width: '0.5rem',
                          height: '0.5rem',
                          borderRadius: '50%',
                          marginTop: '0.5rem',
                          flexShrink: 0,
                          backgroundColor: 'black'
                        }}></div>
                        <p style={{
                          fontSize: '0.875rem',
                          color: '#6b7280'
                        }}>Income data sourced from World Bank and OECD statistics</p>
                      </div>
                      <div style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '0.75rem'
                      }}>
                        <div style={{
                          width: '0.5rem',
                          height: '0.5rem',
                          borderRadius: '50%',
                          marginTop: '0.5rem',
                          flexShrink: 0,
                          backgroundColor: 'black'
                        }}></div>
                        <p style={{
                          fontSize: '0.875rem',
                          color: '#6b7280'
                        }}>All values converted to USD using current exchange rates for consistency</p>
                      </div>
                      <div style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '0.75rem'
                      }}>
                        <div style={{
                          width: '0.5rem',
                          height: '0.5rem',
                          borderRadius: '50%',
                          marginTop: '0.5rem',
                          flexShrink: 0,
                          backgroundColor: 'black'
                        }}></div>
                        <p style={{
                          fontSize: '0.875rem',
                          color: '#6b7280'
                        }}>Countries randomly selected from each income category to ensure diverse comparisons</p>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h3 style={{
                      fontSize: '1.125rem',
                      fontWeight: '600',
                      color: '#1f2937',
                      marginBottom: '1rem'
                    }}>Global Wealth Distribution Facts</h3>
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.75rem'
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '0.75rem'
                      }}>
                        <div style={{
                          width: '0.5rem',
                          height: '0.5rem',
                          borderRadius: '50%',
                          marginTop: '0.5rem',
                          flexShrink: 0,
                          backgroundColor: 'black'
                        }}></div>
                        <p style={{
                          fontSize: '0.875rem',
                          color: '#6b7280'
                        }}>Global median wealth per adult is approximately $8,360 USD</p>
                      </div>
                      <div style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '0.75rem'
                      }}>
                        <div style={{
                          width: '0.5rem',
                          height: '0.5rem',
                          borderRadius: '50%',
                          marginTop: '0.5rem',
                          flexShrink: 0,
                          backgroundColor: 'black'
                        }}></div>
                        <p style={{
                          fontSize: '0.875rem',
                          color: '#6b7280'
                        }}>Top 10% of adults globally own 81% of all wealth</p>
                      </div>
                      <div style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '0.75rem'
                      }}>
                        <div style={{
                          width: '0.5rem',
                          height: '0.5rem',
                          borderRadius: '50%',
                          marginTop: '0.5rem',
                          flexShrink: 0,
                          backgroundColor: 'black'
                        }}></div>
                        <p style={{
                          fontSize: '0.875rem',
                          color: '#6b7280'
                        }}>Bottom 50% of global population owns just 2% of total wealth</p>
                      </div>
                      <div style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '0.75rem'
                      }}>
                        <div style={{
                          width: '0.5rem',
                          height: '0.5rem',
                          borderRadius: '50%',
                          marginTop: '0.5rem',
                          flexShrink: 0,
                          backgroundColor: 'black'
                        }}></div>
                        <p style={{
                          fontSize: '0.875rem',
                          color: '#6b7280'
                        }}>Wealth inequality is significantly higher than income inequality globally</p>
                      </div>
                      <div style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '0.75rem'
                      }}>
                        <div style={{
                          width: '0.5rem',
                          height: '0.5rem',
                          borderRadius: '50%',
                          marginTop: '0.5rem',
                          flexShrink: 0,
                          backgroundColor: 'black'
                        }}></div>
                        <p style={{
                          fontSize: '0.875rem',
                          color: '#6b7280'
                        }}>Your relative position varies dramatically between countries due to economic development differences</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div style={{
                  marginTop: '2rem',
                  padding: '1.5rem',
                  backgroundColor: 'white',
                  borderRadius: '0.75rem',
                  border: '1px solid #006130'
                }}>
                  <h4 style={{
                    fontWeight: '600',
                    color: 'black',
                    marginBottom: '0.5rem'
                  }}>Important Considerations</h4>
                  <p style={{
                    fontSize: '0.875rem',
                    color: 'black'
                  }}>
                    This analysis provides statistical positioning based on available data. Individual financial circumstances, 
                    cost of living variations, social benefits, and economic opportunities differ significantly across regions. 
                    Use these insights as one perspective in understanding global economic inequality and personal financial planning.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WealthComparisonApp;